import { useTranslation } from "react-i18next";
import type { ProjectSummary } from "../../../api/projectsApi";
import HoverHint from "../../../ui/feedback/HoverHint";
import OptionLabel from "../../../ui/forms/OptionLabel";
import { ruleName } from "../settingsTexts";

interface LibraryPinningFieldProps {
  /** `null`: every project may pin this project as a library. */
  pinningProjects: readonly string[] | null;
  /** The projects that can be listed. Omitted for the installation's defaults, which list none. */
  choices?: readonly ProjectSummary[];
  /** Omitted: shown read-only. */
  onChange?: (pinningProjects: readonly string[] | null) => void;
}

/**
 * The rule `pinning_projects` (JD-2): once this project is a library, which projects may pin
 * it. Off, every project, by whoever can read the library. A listed project the user cannot
 * see reads "Unavailable project", its ID only in the row's details (its title).
 */
export default function LibraryPinningField({ pinningProjects, choices, onChange }: LibraryPinningFieldProps) {
  const { t } = useTranslation();
  const listed = new Set(pinningProjects ?? []);
  const visible = new Set(choices?.map((project) => project.projectId));
  const rows = [
    ...(choices ?? []).map((project) => ({ id: project.projectId, name: project.name, hidden: false })),
    ...[...listed].filter((id) => !visible.has(id)).map((id) => ({ id, name: t("projects.settings.rules.pinningProjectOther"), hidden: true })),
  ];
  const setListed = (id: string, on: boolean) => {
    const next = new Set(listed);
    if (on) next.add(id);
    else next.delete(id);
    onChange?.([...next].sort());
  };

  return (
    <div className="space-y-1">
      <p className="app-label flex items-center gap-1">
        {ruleName(t, "pinning_projects")}
        <HoverHint name={ruleName(t, "pinning_projects")}>
          {t(choices === undefined ? "projects.settings.rules.pinningProjectsDefaultsHint" : "projects.settings.rules.pinningProjectsHint")}
        </HoverHint>
      </p>
      <OptionLabel>
        <input type="checkbox" checked={pinningProjects !== null} disabled={onChange === undefined}
          onChange={(event) => { onChange?.(event.target.checked ? [] : null); }} />
        {t("projects.settings.rules.pinningProjectsOnly")}
      </OptionLabel>
      {pinningProjects !== null && choices !== undefined ? (
        <ul className="space-y-1 pl-6">
          {rows.map((row) => (
            <li key={row.id} title={row.hidden ? row.id : undefined}>
              <OptionLabel>
                <input type="checkbox" checked={listed.has(row.id)} disabled={onChange === undefined}
                  onChange={(event) => { setListed(row.id, event.target.checked); }} />
                {row.name}
              </OptionLabel>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
